import { withRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@blaze-cms/nextjs-components';
import { MdMenu, MdClose } from 'react-icons/md';
import Search from '../Search';
import { UserMenuDesktop, UserMenuMobile } from './UserMenu';

const STICKY_ROW_CLASS = 'row--sticky';
const DEFAULT_BANNER_HIDE_TIME = 300;

const SECTIONS = {
  YACHTS: 'yachts',
  FOR_SALE: 'for_sale',
  CHARTER: 'charter',
  DESTINATIONS: 'destinations',
  BOAT_LIFE: 'boat_life',
  PARTNER_CONTENT: 'partner_content',
  EVENTS: 'events',
  BOAT_PRO: 'boat_pro',
  USER: 'user'
};

const ROOT_SELECTOR = 'header';

const B = {
  ITEM: 'item',
  ITEM_LINK: 'item-link',
  ITEM_TOGGLER: 'item-toggler',
  ITEMS: 'items',
  LOGO_IMAGE: 'logo-image',
  LOGO_LINK: 'logo-link',
  MAIN_LINK: 'main-link',
  MAIN_MENU: 'main-menu',
  MENUS: 'menus',
  MOBILE_BUTTON: 'mobile-button',
  MOBILE_SUBNAVBAR: 'mobile-subnavbar',
  SEARCH: 'search',
  SECONDARY_MENU: 'secondary-menu',
  STRAPLINE: 'strapline',
  SUBITEM: 'subitem',
  SUBITEM_LINK: 'subitem-link',
  SUBITEMS: 'subitems',
  SUBITEMS_BLOCK: 'subitems-block',
  TITLE: 'title',
  WRAPPER: 'wrapper'
};

const M = {
  BOAT_LIFE: 'boat-life',
  BOAT_PRO: 'boat-pro',
  CHARTER: 'charter',
  DESTINATIONS: 'destinations',
  EVENTS: 'events',
  FOR_SALE: 'for-sale',
  HUGE_SUBITEMS: 'huge-subitems',
  MEDIUM_SUBITEMS: 'medium-subitems',
  MOBILE_ONLY: 'mobile-only',
  NEWSLETTERS: 'newsletters',
  NO_SUBITEMS: 'no-subitems',
  PARTNER_CONTENT: 'partner-content',
  ROOT: 'root',
  SHOP: 'shop',
  SUBITEMS_RIGHT: 'subitems-right',
  SUBSCRIBE: 'subscribe',
  THIN_SUBITEMS: 'thin-subitems',
  VBS: 'vbs',
  YACHTS: 'yachts',
  USER: 'user'
};

const buildClass = (blocks, modifiers = []) => {
  const base = [ROOT_SELECTOR, blocks.join('__')].join('__');

  return [...[base], ...modifiers.map(modifier => `${base}--${modifier}`)].join(' ');
};

const CLASSES = {
  LOGO_IMAGE: buildClass([B.LOGO_IMAGE]),
  LOGO_LINK: buildClass([B.LOGO_LINK]),
  MAIN_MENU: buildClass([B.MAIN_MENU]),
  MAIN_MENU_ITEM_BOAT_LIFE: buildClass([B.MAIN_MENU, B.ITEM], [M.BOAT_LIFE, M.MEDIUM_SUBITEMS]),
  MAIN_MENU_ITEM_BOAT_PRO: buildClass([B.MAIN_MENU, B.ITEM], [M.BOAT_PRO, M.SUBITEMS_RIGHT]),
  MAIN_MENU_ITEM_CHARTER: buildClass([B.MAIN_MENU, B.ITEM], [M.CHARTER, M.THIN_SUBITEMS]),
  MAIN_MENU_ITEM_DESTINATIONS: buildClass([B.MAIN_MENU, B.ITEM], [M.DESTINATIONS, M.THIN_SUBITEMS]),
  MAIN_MENU_ITEM_EVENTS: buildClass([B.MAIN_MENU, B.ITEM], [M.EVENTS]),
  MAIN_MENU_ITEM_FOR_SALE: buildClass([B.MAIN_MENU, B.ITEM], [M.FOR_SALE, M.THIN_SUBITEMS]),
  MAIN_MENU_ITEM_LINK: buildClass([B.MAIN_MENU, B.ITEM_LINK]),
  MAIN_MENU_ITEM_NEWSLETTERS: buildClass([B.MAIN_MENU, B.ITEM], [M.NEWSLETTERS, M.MOBILE_ONLY]),
  MAIN_MENU_ITEM_USER: buildClass([B.MAIN_MENU, B.ITEM], [M.USER, M.MOBILE_ONLY]),
  MAIN_MENU_ITEM_PARTNER_CONTENT: buildClass(
    [B.MAIN_MENU, B.ITEM],
    [M.PARTNER_CONTENT, M.THIN_SUBITEMS, M.SUBITEMS_RIGHT]
  ),
  MAIN_MENU_ITEM_SHOP: buildClass([B.MAIN_MENU, B.ITEM], [M.SHOP, M.MOBILE_ONLY]),
  MAIN_MENU_ITEM_SUBSCRIBE: buildClass([B.MAIN_MENU, B.ITEM], [M.SUBSCRIBE, M.MOBILE_ONLY]),
  MAIN_MENU_ITEM_TOGGLER: buildClass([B.MAIN_MENU, B.ITEM_TOGGLER]),
  MAIN_MENU_ITEM_YACHTS: buildClass([B.MAIN_MENU, B.ITEM], [M.YACHTS, M.HUGE_SUBITEMS]),
  MAIN_MENU_SUBITEM: buildClass([B.MAIN_MENU, B.SUBITEM]),
  MAIN_MENU_SUBITEM_LINK: buildClass([B.MAIN_MENU, B.SUBITEM_LINK]),
  MAIN_MENU_SUBITEM_ROOT: buildClass([B.MAIN_MENU, B.SUBITEM], [M.ROOT]),
  MAIN_MENU_SUBITEMS: buildClass([B.MAIN_MENU, B.SUBITEMS]),
  MAIN_MENU_SUBITEMS_BLOCK: buildClass([B.MAIN_MENU, B.SUBITEMS_BLOCK]),
  MENUS: buildClass([B.MENUS]),
  MOBILE_BUTTON: buildClass([B.MOBILE_BUTTON]),
  MOBILE_SUBNAVBAR_BOAT_PRO: buildClass([B.MOBILE_SUBNAVBAR], [M.BOAT_PRO]),
  MOBILE_SUBNAVBAR_ITEM: buildClass([B.MOBILE_SUBNAVBAR, B.ITEM]),
  MOBILE_SUBNAVBAR_ITEM_LINK: buildClass([B.MOBILE_SUBNAVBAR, B.ITEM_LINK]),
  MOBILE_SUBNAVBAR_ITEMS: buildClass([B.MOBILE_SUBNAVBAR, B.ITEMS]),
  MOBILE_SUBNAVBAR_MAIN_LINK: buildClass([B.MOBILE_SUBNAVBAR, B.MAIN_LINK]),
  MOBILE_SUBNAVBAR_TITLE: buildClass([B.MOBILE_SUBNAVBAR, B.TITLE]),
  MOBILE_SUBNAVBAR_VBS: buildClass([B.MOBILE_SUBNAVBAR], [M.VBS]),
  WRAPPER: buildClass([B.WRAPPER]),
  SEARCH: buildClass([B.SEARCH]),
  SECONDARY_MENU: buildClass([B.SECONDARY_MENU]),
  SECONDARY_MENU_ITEM: buildClass([B.SECONDARY_MENU, B.ITEM]),
  SECONDARY_MENU_ITEM_LINK: buildClass([B.SECONDARY_MENU, B.ITEM_LINK]),
  SECONDARY_MENU_WRAPPER: buildClass([B.SECONDARY_MENU, B.WRAPPER]),
  STRAPLINE: buildClass([B.STRAPLINE])
};

const Header = ({ router, bannerHideTime, onlyStickBannerRowOnce }) => {
  const ref = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const toggleSubmenu = section => setOpenSubmenu(openSubmenu === section ? null : section);

  useEffect(
    () => {
      // handle dynamic sticky banner
      if (ref && ref.current) {
        const headerElement = ref.current;
        let bannerRowElement = headerElement.previousElementSibling;

        let resizeObserver;
        let classCheck;

        const addChecks = () => {
          if (bannerRowElement) {
            resizeObserver = new ResizeObserver(() => {
              if (bannerRowElement && bannerRowElement.classList.contains(STICKY_ROW_CLASS)) {
                // set top to banner height
                headerElement.style.top = `${bannerRowElement.clientHeight}px`;
              }
            });
            resizeObserver.observe(bannerRowElement);

            classCheck = new MutationObserver(() => {
              if (bannerRowElement && !bannerRowElement.classList.contains(STICKY_ROW_CLASS)) {
                const bannerHideTimeToUse =
                  bannerHideTime === null ? DEFAULT_BANNER_HIDE_TIME : bannerHideTime;
                // reset top if banner row is no longer sticky
                if (bannerHideTimeToUse === 0) {
                  headerElement.style.top = '';
                  return;
                }

                let height = bannerRowElement.offsetHeight;

                if (window.scrollY === 0) {
                  // not moved so don't do anything
                  headerElement.style.top = '';
                  return;
                }

                let propertyToReduce = 'height';
                // don't hide entire banner if still should be in view
                if (window.scrollY < height) {
                  height = window.scrollY;
                  propertyToReduce = 'marginTop';
                }

                bannerRowElement.style.position = 'sticky';
                bannerRowElement.style.top = 0;
                bannerRowElement.style['z-index'] = 120;

                let interval = Math.ceil(height / bannerHideTimeToUse);
                interval = interval < 6 ? 6 : interval;
                let newHeight = height;

                // add scroll offset
                if (propertyToReduce === 'marginTop')
                  bannerRowElement.style.marginTop = `${window.scrollY}px`;
                const intervalTimer = setInterval(() => {
                  newHeight -= interval;
                  // if past scroll offset or at 0 stop
                  if (newHeight <= 0) {
                    // newHeight >= (window.scrollY * 2) ||
                    headerElement.style.top = '';
                    bannerRowElement.style.height = '';
                    bannerRowElement.style.position = '';
                    bannerRowElement.style.height = '';
                    bannerRowElement.style.marginTop = '';
                    clearInterval(intervalTimer);
                    return;
                  }
                  headerElement.style.top = `${newHeight}px`;
                  bannerRowElement.style[propertyToReduce] = `${newHeight}px`;
                }, interval);
              }
            });
            classCheck.observe(bannerRowElement, { attributes: true, attributeFilter: ['class'] });
          } else {
            // set default top if not banner row
            headerElement.style.top = '';
          }
        };

        const bannerRowCheck = new MutationObserver(() => {
          if (bannerRowElement !== headerElement.previousElementSibling) {
            // diable sticky on follow up pages
            if (
              onlyStickBannerRowOnce &&
              bannerRowElement &&
              headerElement.previousElementSibling
            ) {
              headerElement.previousElementSibling.classList.remove(STICKY_ROW_CLASS);
              return;
            }

            // update if banner row changes
            bannerRowElement = headerElement.previousElementSibling;

            addChecks();
          }
        });
        bannerRowCheck.observe(headerElement.parentNode, { childList: true });

        addChecks();

        return () => {
          classCheck && classCheck.disconnect();
          resizeObserver && resizeObserver.disconnect();
        };
      }
    },
    [bannerHideTime, onlyStickBannerRowOnce]
  );

  return (
    <div
      ref={ref}
      className={
        isMobileMenuOpen ? `${ROOT_SELECTOR} ${ROOT_SELECTOR}--mobile-open` : ROOT_SELECTOR
      }>
      <div className={CLASSES.WRAPPER}>
        {!isMobileMenuOpen && (
          <div className={CLASSES.MOBILE_BUTTON} onClick={() => setIsMobileMenuOpen(true)}>
            <i>
              <MdMenu />
            </i>
          </div>
        )}

        {isMobileMenuOpen && (
          <div className={CLASSES.MOBILE_BUTTON} onClick={() => setIsMobileMenuOpen(false)}>
            <i>
              <MdClose />
            </i>
          </div>
        )}

        <Link href="/" className={CLASSES.LOGO_LINK} onClick={() => setIsMobileMenuOpen(false)}>
          <img className={CLASSES.LOGO_IMAGE} src="/static/img/logo.png" alt="Boat logo" />
        </Link>

        <Search router={router} modifier={CLASSES.SEARCH} />

        <div className={CLASSES.MENUS}>
          <div className={CLASSES.SECONDARY_MENU_WRAPPER}>
            <p className={CLASSES.STRAPLINE}>The global authority in superyachting</p>

            <ul className={CLASSES.SECONDARY_MENU}>
              <li className={CLASSES.SECONDARY_MENU_ITEM}>
                <Link
                  href="https://shop.boatinternational.com/shop"
                  className={CLASSES.SECONDARY_MENU_ITEM_LINK}
                  data-gtm-name="SHOP"
                  target="_blank">
                  SHOP
                </Link>
              </li>

              <li className={CLASSES.SECONDARY_MENU_ITEM}>
                <Link
                  href="/subscriptions"
                  data-gtm-name="SUBSCRIBE"
                  className={CLASSES.SECONDARY_MENU_ITEM_LINK}>
                  SUBSCRIBE
                </Link>
              </li>

              <li className={CLASSES.SECONDARY_MENU_ITEM}>
                <Link
                  href="/newsletters"
                  data-gtm-name="NEWSLETTERS"
                  className={CLASSES.SECONDARY_MENU_ITEM_LINK}>
                  NEWSLETTERS
                </Link>
              </li>

              <UserMenuDesktop className={CLASSES.SECONDARY_MENU_ITEM} />
            </ul>
          </div>

          <ul className={CLASSES.MAIN_MENU}>
            <li className={`${CLASSES.MAIN_MENU_ITEM_YACHTS} bi-channel--yachts`}>
              <Link
                href="/yachts"
                data-gtm-name="YACHTS"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}>
                YACHTS
              </Link>

              <div
                className={CLASSES.MAIN_MENU_ITEM_TOGGLER}
                onClick={() => toggleSubmenu(SECTIONS.YACHTS)}>
                YACHTS
              </div>

              <div
                className={`${
                  CLASSES.MAIN_MENU_SUBITEMS
                } bi-channel--yachts submenu-open-${openSubmenu === SECTIONS.YACHTS}`}>
                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM_ROOT}>
                    <Link
                      href="/yachts"
                      data-gtm-name="Yachts Home"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Yachts Home
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/the-superyacht-directory"
                      data-gtm-name="The Superyacht Directory"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      The Superyacht Directory
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/news"
                      data-gtm-name="Yacht News"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Yacht News
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/editorial-features"
                      data-gtm-name="Yacht Reports"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Yacht Reports
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yacht-market-intelligence/brokerage-sales-news"
                      data-gtm-name="Brokerage News"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Brokerage News
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/podcasts"
                      data-gtm-name="Podcasts"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Podcasts
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/the-register/largest-yachts-in-the-world"
                      data-gtm-name="The largest yachts in the world"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      The largest yachts in the world
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/the-register"
                      data-gtm-name="The Register"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      The Register
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/luxury-yacht-advice"
                      data-gtm-name="Yacht Advice"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Yacht Advice
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/monaco-yacht-show"
                      data-gtm-name="Monaco Yacht Show"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Monaco Yacht Show
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/fort-lauderdale-international-boat-show"
                      data-gtm-name="FLIBS"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      FLIBS
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/cannes-yachting-festival"
                      data-gtm-name="Cannes Yachting Festival"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Cannes Yachting Festival
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/palm-beach-international-boat-show"
                      data-gtm-name="Palm Beach Boat Show"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Palm Beach Boat Show
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/yacht-design"
                      data-gtm-name="Yacht Design"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Yacht Design
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/12-24"
                      data-gtm-name="12m to 24m yachts"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      12m to 24m yachts
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/profiles"
                      data-gtm-name="Builder & Designer Directory"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Builder & Designer Directory
                    </Link>
                  </li>
                </ul>
              </div>
            </li>

            <li className={`${CLASSES.MAIN_MENU_ITEM_FOR_SALE} bi-channel--yfs`}>
              <Link
                href="/yachts-for-sale"
                data-gtm-name="YACHTS FOR SALE"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}>
                YACHTS FOR SALE
              </Link>

              <div
                className={CLASSES.MAIN_MENU_ITEM_TOGGLER}
                onClick={() => toggleSubmenu(SECTIONS.FOR_SALE)}>
                YACHTS FOR SALE
              </div>

              <div
                className={`${
                  CLASSES.MAIN_MENU_SUBITEMS
                } bi-channel--yfs submenu-open-${openSubmenu === SECTIONS.FOR_SALE}`}>
                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM_ROOT}>
                    <Link
                      href="/yachts-for-sale"
                      data-gtm-name="Yachts for sale home"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Yachts for sale home
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts-for-sale/motor-yachts"
                      data-gtm-name="Motor yachts"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Motor yachts
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts-for-sale/sailing-yachts"
                      data-gtm-name="Sailing yachts"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Sailing yachts
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts-for-sale/explorer-yachts"
                      data-gtm-name="Explorer yachts"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Explorer yachts
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts-for-sale/classic-yachts"
                      data-gtm-name="Classic yachts"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Classic yachts
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/luxury-yacht-broker-directory?listType=For%20Sale"
                      data-gtm-name="Sale Broker Directory"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Sale Broker Directory
                    </Link>
                  </li>
                </ul>
              </div>
            </li>

            <li className={`${CLASSES.MAIN_MENU_ITEM_CHARTER} bi-channel--yfc`}>
              <Link
                href="/charter"
                data-gtm-name="CHARTER"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}>
                CHARTER
              </Link>

              <div
                className={CLASSES.MAIN_MENU_ITEM_TOGGLER}
                onClick={() => toggleSubmenu(SECTIONS.CHARTER)}>
                CHARTER
              </div>

              <div
                className={`${
                  CLASSES.MAIN_MENU_SUBITEMS
                } bi-channel--yfc submenu-open-${openSubmenu === SECTIONS.CHARTER}`}>
                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM_ROOT}>
                    <Link
                      href="/charter"
                      data-gtm-name="Charter Home"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Charter Home
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/charter/luxury-yachts-for-charter"
                      data-gtm-name="Yachts for Charter"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Yachts for Charter
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/charter/charter-destinations"
                      data-gtm-name="Charter Advice"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Charter Destinations
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/yachts/luxury-yacht-broker-directory?listType=For%20Charter"
                      data-gtm-name="Charter Broker Directory"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Charter Broker Directory
                    </Link>
                  </li>
                </ul>
              </div>
            </li>

            <li className={`${CLASSES.MAIN_MENU_ITEM_DESTINATIONS} bi-channel--destinations`}>
              <Link
                href="/destinations"
                data-gtm-name="DESTINATIONS"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}>
                DESTINATIONS
              </Link>

              <div
                className={CLASSES.MAIN_MENU_ITEM_TOGGLER}
                onClick={() => toggleSubmenu(SECTIONS.DESTINATIONS)}>
                DESTINATIONS
              </div>

              <div
                className={`${
                  CLASSES.MAIN_MENU_SUBITEMS
                } bi-channel--destinations submenu-open-${openSubmenu === SECTIONS.DESTINATIONS}`}>
                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM_ROOT}>
                    <Link
                      href="/destinations"
                      data-gtm-name="Destinations Home"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Destinations Home
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/destinations/mediterranean-yacht-destinations"
                      data-gtm-name="Mediterranean"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Mediterranean
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/destinations/caribbean-yacht-destinations"
                      data-gtm-name="Caribbean"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Caribbean
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/destinations/americas-yacht-destinations"
                      data-gtm-name="Americas"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Americas
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/destinations/south-pacific"
                      data-gtm-name="South Pacific"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      South Pacific
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/destinations/rest-of-the-world-yacht-destinations"
                      data-gtm-name="Rest of the World"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Rest of the World
                    </Link>
                  </li>
                </ul>
              </div>
            </li>

            <li className={`${CLASSES.MAIN_MENU_ITEM_BOAT_LIFE} bi-channel--boat-life`}>
              <Link
                href="/luxury-yacht-life"
                data-gtm-name="BOAT LIFE"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}>
                BOAT LIFE
              </Link>

              <div
                className={CLASSES.MAIN_MENU_ITEM_TOGGLER}
                onClick={() => toggleSubmenu(SECTIONS.BOAT_LIFE)}>
                BOAT LIFE
              </div>

              <div
                className={`${
                  CLASSES.MAIN_MENU_SUBITEMS
                } bi-channel--boat-life submenu-open-${openSubmenu === SECTIONS.BOAT_LIFE}`}>
                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM_ROOT}>
                    <Link
                      href="/luxury-yacht-life"
                      data-gtm-name="Boat Life Home"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Boat Life Home
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-life/lifestyle"
                      data-gtm-name="Lifestyle"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Lifestyle
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-life/owners-experiences"
                      data-gtm-name="Owners' Experiences"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Owners' Experiences
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-life/interiors"
                      data-gtm-name="Interiors"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Interiors
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-life/property"
                      data-gtm-name="Property"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Property
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-life/marine-life"
                      data-gtm-name="Conservation and Philanthropy"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Conservation and Philanthropy
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-life/interiors/directory"
                      data-gtm-name="Interiors Suppliers"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Interiors Suppliers
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-life/owners-club"
                      data-gtm-name="Owners' Club"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Owners' Club
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/captains-club"
                      data-gtm-name="Captains' Club"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Captains' Club
                    </Link>
                  </li>
                </ul>
              </div>
            </li>

            <li className={`${CLASSES.MAIN_MENU_ITEM_PARTNER_CONTENT} bi-channel--vbs`}>
              <span className={CLASSES.MAIN_MENU_ITEM_LINK}>PARTNER CONTENT</span>

              <div
                className={CLASSES.MAIN_MENU_ITEM_TOGGLER}
                onClick={() => toggleSubmenu(SECTIONS.PARTNER_CONTENT)}>
                PARTNER CONTENT
              </div>

              <div
                className={`${
                  CLASSES.MAIN_MENU_SUBITEMS
                } bi-channel--vbs submenu-open-${openSubmenu === SECTIONS.PARTNER_CONTENT}`}>
                <ul
                  className={`${CLASSES.MAIN_MENU_SUBITEMS_BLOCK} ${
                    CLASSES.MAIN_MENU_SUBITEMS_BLOCK
                  }--${M.PARTNER_CONTENT}`}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/showcase"
                      data-gtm-name="BOAT Showcase"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      BOAT Showcase
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-presents"
                      data-gtm-name="BOAT Presents"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      BOAT Presents
                    </Link>
                  </li>
                </ul>
              </div>
            </li>

            <li className={`${CLASSES.MAIN_MENU_ITEM_EVENTS} bi-channel--events`}>
              <Link
                href="/luxury-yacht-events"
                data-gtm-name="EVENTS"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}>
                EVENTS
              </Link>

              <div
                className={CLASSES.MAIN_MENU_ITEM_TOGGLER}
                onClick={() => toggleSubmenu(SECTIONS.EVENTS)}>
                EVENTS
              </div>

              <div
                className={`${
                  CLASSES.MAIN_MENU_SUBITEMS
                } bi-channel--events submenu-open-${openSubmenu === SECTIONS.EVENTS}`}>
                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM_ROOT}>
                    <Link
                      href="/luxury-yacht-events"
                      data-gtm-name="Events Home"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Events Home
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/world-superyacht-awards"
                      data-gtm-name="World Superyacht Awards"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      World Superyacht Awards
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/superyacht-design-festival"
                      data-gtm-name="Superyacht Design Festival"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Superyacht Design Festival
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/design-and-innovation-awards"
                      data-gtm-name="Design and Innovation Awards"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Design and Innovation Awards
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/young-designer-of-the-year-award"
                      data-gtm-name="Young Designer of the Year Award"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Young Designer of the Year Award
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/boat-artistry-and-craft-awards"
                      data-gtm-name="Artistry and Craft Awards"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Artistry and Craft Awards
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/explorer-yachts-summit"
                      data-gtm-name="Explorer Yachts Summit"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Explorer Yachts Summit
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/ocean-talks"
                      data-gtm-name="Ocean Talks"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Ocean Talks
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/ocean-awards"
                      data-gtm-name="The Ocean Awards"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      The Ocean Awards
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/boat-connect"
                      data-gtm-name="BOAT Connect"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      BOAT Connect
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/between-the-bays"
                      data-gtm-name="Between the bays"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Between the bays
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/golf-invitational"
                      data-gtm-name="Golf Invitational"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Golf Invitational
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/luxury-yacht-events/bermuda-rendezvous"
                      data-gtm-name="Bermuda Rendezvous"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Bermuda Rendezvous
                    </Link>
                  </li>
                </ul>
              </div>
            </li>

            <li className={`${CLASSES.MAIN_MENU_ITEM_BOAT_PRO} bi-channel--boat-pro`}>
              <Link
                href="/boat-pro"
                data-gtm-name="BOATPRO"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}>
                BOATPRO
              </Link>

              <div
                className={CLASSES.MAIN_MENU_ITEM_TOGGLER}
                onClick={() => toggleSubmenu(SECTIONS.BOAT_PRO)}>
                BOATPRO
              </div>

              <div
                className={`${
                  CLASSES.MAIN_MENU_SUBITEMS
                } bi-channel--boat-pro submenu-open-${openSubmenu === SECTIONS.BOAT_PRO}`}>
                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM_ROOT}>
                    <Link
                      href="/boat-pro"
                      data-gtm-name="BOATPro Home"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      BOATPro Home
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-pro/news"
                      data-gtm-name="News"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      News
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-pro/superyacht-insight"
                      data-gtm-name="Superyacht Insight"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Superyacht Insight
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-pro/global-order-book"
                      data-gtm-name="Global Order Book"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Global Order Book
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-pro/premium"
                      data-gtm-name="Premium Content"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Premium Content
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-pro/features"
                      data-gtm-name="Product Features"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Product Features
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-pro/testimonials"
                      data-gtm-name="Testimonials"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Testimonials
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-pro/pricing"
                      data-gtm-name="Pricing Plan"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Pricing Plan
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-pro/mobile-app"
                      data-gtm-name="Mobile App"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Mobile App
                    </Link>
                  </li>

                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="/boat-pro/contact"
                      data-gtm-name="Contact Us"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Contact Us
                    </Link>
                  </li>
                </ul>

                <ul className={CLASSES.MAIN_MENU_SUBITEMS_BLOCK}>
                  <li className={CLASSES.MAIN_MENU_SUBITEM}>
                    <Link
                      href="https://pro.boatinternational.com/"
                      data-gtm-name="Subscriber Login"
                      className={CLASSES.MAIN_MENU_SUBITEM_LINK}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      Subscriber Login
                    </Link>
                  </li>
                </ul>
              </div>
            </li>

            <li className={CLASSES.MAIN_MENU_ITEM_SHOP}>
              <Link
                href="https://shop.boatinternational.com/shop"
                data-gtm-name="SHOP"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}
                target="_blank">
                SHOP
              </Link>
            </li>

            <li className={CLASSES.MAIN_MENU_ITEM_SUBSCRIBE}>
              <Link
                href="/subscriptions"
                data-gtm-name="SUBSCRIBE"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}>
                SUBSCRIBE
              </Link>
            </li>

            <li className={CLASSES.MAIN_MENU_ITEM_NEWSLETTERS}>
              <Link
                href="/newsletters"
                data-gtm-name="NEWSLETTERS"
                className={CLASSES.MAIN_MENU_ITEM_LINK}
                onClick={() => setIsMobileMenuOpen(false)}>
                NEWSLETTERS
              </Link>
            </li>

            <UserMenuMobile
              className={CLASSES.MAIN_MENU_ITEM_USER}
              toggleSubmenu={toggleSubmenu}
              openSubmenu={openSubmenu}
              CLASSES={CLASSES}
              section={SECTIONS.USER}
            />
          </ul>
        </div>
      </div>

      <div className={CLASSES.MOBILE_SUBNAVBAR_BOAT_PRO}>
        <div className={CLASSES.MOBILE_SUBNAVBAR_TITLE}>
          <Link
            href="/boat-pro"
            data-gtm-name="BOATPro"
            className={CLASSES.MOBILE_SUBNAVBAR_MAIN_LINK}>
            BOATPro
          </Link>
        </div>

        <ul className={CLASSES.MOBILE_SUBNAVBAR_ITEMS}>
          <li className={CLASSES.MOBILE_SUBNAVBAR_ITEM}>
            <Link
              href="/boat-pro/news"
              data-gtm-name="News"
              className={CLASSES.MOBILE_SUBNAVBAR_ITEM_LINK}>
              News
            </Link>
          </li>

          <li className={CLASSES.MOBILE_SUBNAVBAR_ITEM}>
            <Link
              href="/boat-pro/superyacht-insight"
              data-gtm-name="Insight"
              className={CLASSES.MOBILE_SUBNAVBAR_ITEM_LINK}>
              Insight
            </Link>
          </li>

          <li className={CLASSES.MOBILE_SUBNAVBAR_ITEM}>
            <Link
              href="/boat-pro/global-order-book"
              data-gtm-name="Order Book"
              className={CLASSES.MOBILE_SUBNAVBAR_ITEM_LINK}>
              Order Book
            </Link>
          </li>

          <li className={CLASSES.MOBILE_SUBNAVBAR_ITEM}>
            <Link
              href="/boat-pro/pricing"
              data-gtm-name="Pricing Plan"
              className={CLASSES.MOBILE_SUBNAVBAR_ITEM_LINK}>
              Pricing Plan
            </Link>
          </li>

          <li className={CLASSES.MOBILE_SUBNAVBAR_ITEM}>
            <Link
              href="/boat-pro/features"
              data-gtm-name="Features"
              className={CLASSES.MOBILE_SUBNAVBAR_ITEM_LINK}>
              Features
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};

Header.propTypes = {
  bannerHideTime: PropTypes.number,
  onlyStickBannerRowOnce: PropTypes.bool
};

Header.defaultProps = {
  bannerHideTime: DEFAULT_BANNER_HIDE_TIME,
  onlyStickBannerRowOnce: false
};

export default withRouter(Header);
